// src/components/LanguageSwitch.jsx
'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { setAppLanguage } from '@/i18n';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Compact EN/ES pill toggle styled to match the lime/pitch design
 * system (pitch-dark surfaces with lime accent — same palette as the
 * sidebar's "PRO"/"COACH" pills, the auth-page CTAs, and the team-
 * scoped chrome).
 *
 * Lives in the top bar on auth pages (AuthShell) and persists choice
 * to localStorage via setAppLanguage. Once the user signs in,
 * ProfileSettings can mirror it onto the Firestore profile.
 */
export default function LanguageSwitch({ className = '' }) {
  const { i18n } = useTranslation();
  const current = String(i18n.language || 'en').toLowerCase().startsWith('es')
    ? 'es'
    : 'en';

  const pick = (lang) => {
    if (lang === current) return;
    setAppLanguage(lang);
  };

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full bg-[#162b1e] ring-1 ring-white/10 px-1 py-1 shadow-[0_1px_2px_0_rgba(13,31,20,0.4)] ${className}`}
      role="group"
      aria-label="Language"
    >
      <Languages className="w-3.5 h-3.5 text-[#b5f03d]/60 mx-1.5" aria-hidden="true" />
      {['en', 'es'].map((code) => {
        const active = current === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => pick(code)}
            aria-pressed={active}
            // Active = filled lime pill with pitch text. Inactive =
            // muted white that lifts on hover/press for affordance.
            // Letter-spacing + Barlow Condensed mirrors the COACH/PRO
            // role pills in the sidebar.
            className={[
              'px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.6px] transition-colors',
              active
                ? 'bg-[#b5f03d] text-[#0d1f14]'
                : 'text-white/55 hover:text-white/85 hover:bg-white/5',
            ].join(' ')}
            style={{ fontFamily: HEAD_FONT }}
          >
            {code === 'en' ? 'EN' : 'ES'}
          </button>
        );
      })}
    </div>
  );
}
